/**
 * Serviço central: metas e comissões do dashboard ← desempenho_config
 */

import { supabase } from '@/integrations/supabase/client';
import {
  TipoDesempenho,
  TIPO_DESEMPENHO_DEFAULTS,
  TIPO_DESEMPENHO_LABELS,
  GRUPO_DESEMPENHO_LABELS,
  CAMPOS_META_POR_TIPO,
  formatPeriodoProducao,
  GrupoDesempenho,
  DesempenhoFaixa,
  tipoUsaFaixas,
  labelFaixaCurta,
} from '@/features/superadmin/types/desempenho';
import { getProductionPeriod } from '@/utils/productionPeriod';

export interface DesempenhoIndicadores {
  tipoDesempenho: TipoDesempenho;
  configId: string | null;
  metaValor: number;
  metaReunioes: number;
  metaFixacao: number;
  comissaoPercentualBase: number;
  bonusFaixaPercentual: number;
  periodoLabel: string | null;
  temConfigCadastrada: boolean;
  faixaAtingida?: DesempenhoFaixa | null;
  faixas?: DesempenhoFaixa[];
}

export const ROLE_TO_TIPO_DESEMPENHO: Record<string, TipoDesempenho> = {
  RELACIONADOR: 'RELACIONADOR',
  LIDER_VENDAS: 'LIDER_VENDAS',
  SUPERVISOR: 'SUPERVISOR',
  POS_VENDA: 'POS_VENDA',
  ASSOCIADO: 'ASSOCIADO',
  REPRESENTANTE: 'REPRESENTANTE',
  SECRETARIA: 'SUPERVISOR',
};

export function calcularAtingimento(realizado: number, meta: number): number {
  if (!meta || meta <= 0) return 0;
  return Math.round((realizado / meta) * 10000) / 100;
}

export function calcularComissao(
  valorBase: number,
  indicadores: DesempenhoIndicadores,
  _atingiuMeta?: boolean
): number {
  // Bônus de faixa é uma premiação independente e nunca compõe a comissão.
  return (valorBase * indicadores.comissaoPercentualBase) / 100;
}

function indicadoresFromRow(row: Record<string, unknown> | null, tipo: TipoDesempenho): DesempenhoIndicadores {
  const defaults = TIPO_DESEMPENHO_DEFAULTS[tipo];

  if (!row) {
    return {
      tipoDesempenho: tipo,
      configId: null,
      metaValor: defaults.meta_valor ?? 0,
      metaReunioes: defaults.meta_reunioes ?? 0,
      metaFixacao: defaults.meta_fixacao ?? 0,
      comissaoPercentualBase: defaults.comissao_percentual_base ?? 0,
      bonusFaixaPercentual: 0,
      periodoLabel: null,
      temConfigCadastrada: false,
    };
  }

  return {
    tipoDesempenho: tipo,
    configId: String(row.id),
    metaValor: Number(row.meta_valor ?? defaults.meta_valor ?? 0),
    metaReunioes: Number(row.meta_reunioes ?? defaults.meta_reunioes ?? 0),
    metaFixacao: Number(row.meta_fixacao ?? defaults.meta_fixacao ?? 0),
    comissaoPercentualBase: Number(row.comissao_percentual_base ?? defaults.comissao_percentual_base ?? 0),
    bonusFaixaPercentual: 0,
    periodoLabel: formatPeriodoProducao(Number(row.periodo_mes), Number(row.periodo_ano)),
    temConfigCadastrada: true,
  };
}

export async function buscarIndicadoresDesempenho(
  tenantId: number,
  tipo: TipoDesempenho,
  refDate?: string,
  realizado?: { valor?: number; reunioes?: number; fixacao?: number }
): Promise<DesempenhoIndicadores> {
  const ref = refDate ?? new Date().toISOString().split('T')[0];

  const { data, error } = await supabase.rpc('get_desempenho_config_vigente', {
    p_tenant_id: tenantId,
    p_tipo_desempenho: tipo,
    p_grupo: null,
    p_ref_date: ref,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  const indicadores = indicadoresFromRow(row as Record<string, unknown> | null, tipo);

  if (!indicadores.configId) return indicadores;

  const { data: faixas, error: faixasError } = await supabase.rpc('get_desempenho_faixas_vigente', {
    p_tenant_id: tenantId,
    p_tipo_desempenho: tipo,
    p_grupo: null,
    p_ref_date: ref,
  });
  if (faixasError) throw faixasError;

  const faixasParsed = (faixas || []).map((f: Record<string, unknown>) => parseFaixaRow(f));
  indicadores.faixas = faixasParsed;

  if (realizado && faixasParsed.length > 0) {
    const { data: faixa, error: faixaError } = await supabase.rpc('classificar_faixa_atingida', {
      p_desempenho_config_id: Number(indicadores.configId),
      p_realizado_valor: realizado.valor ?? 0,
      p_realizado_reunioes: realizado.reunioes ?? 0,
      p_realizado_fixacao: realizado.fixacao ?? 0,
    });
    if (faixaError) throw faixaError;
    const faixaRow = Array.isArray(faixa) ? faixa[0] : faixa;
    indicadores.faixaAtingida = faixaRow ? parseFaixaRow(faixaRow as Record<string, unknown>) : null;
    if (indicadores.faixaAtingida?.bonus_percentual != null) {
      indicadores.bonusFaixaPercentual = indicadores.faixaAtingida.bonus_percentual;
    }
  }

  return indicadores;
}

function parseFaixaRow(row: Record<string, unknown>): DesempenhoFaixa {
  return {
    id: String(row.id),
    desempenho_config_id: String(row.desempenho_config_id),
    nivel: row.nivel as DesempenhoFaixa['nivel'],
    classificacao: String(row.classificacao),
    ordem: Number(row.ordem),
    meta_valor: row.meta_valor != null ? Number(row.meta_valor) : undefined,
    meta_reunioes: row.meta_reunioes != null ? Number(row.meta_reunioes) : undefined,
    meta_fixacao: row.meta_fixacao != null ? Number(row.meta_fixacao) : undefined,
    bonus_percentual: row.bonus_percentual != null ? Number(row.bonus_percentual) : undefined,
    created_at: new Date(row.created_at as string),
    updated_at: new Date(row.updated_at as string),
  };
}

export async function contarReunioesRealizadas(
  tenantId: number,
  dataInicio: string,
  dataFim: string,
  consultorId?: number
): Promise<number> {
  let query = supabase
    .from('agendamentos')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('status', 'REALIZADO')
    .gte('data_agendamento', dataInicio)
    .lte('data_agendamento', dataFim);

  if (consultorId) {
    query = query.or(`relacionador_id.eq.${consultorId},lider_vendas_id.eq.${consultorId}`);
  }

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export interface DesempenhoConsolidadoItem {
  configId: string;
  tenantId: number;
  tenantNome: string;
  tenantTipo?: string;
  grupo: GrupoDesempenho;
  tipoDesempenho: TipoDesempenho;
  escopo: 'cargo' | 'individual';
  profileId?: string;
  profileNome?: string;
  periodoLabel: string;
  metaExibicao: string;
  realizadoExibicao: string;
  metaNumerica: number;
  realizadoNumerico: number;
  atingimento: number;
  atingiuMeta: boolean;
  comissaoPercentualBase: number;
  bonusFaixaPercentual: number;
  faixaAtingida?: DesempenhoFaixa | null;
}

export interface DesempenhoConsolidadoResumo {
  periodoLabel: string;
  totalTenants: number;
  tenantsComMetas: number;
  tenantsSemMetas: number;
  totalConfigs: number;
  configsAtivas: number;
  totalAjustesIndividuais: number;
  atingimentoMedio: number;
  metasBatidas: number;
  abaixoDaMeta: number;
  faturamentoConsolidado: number;
  metaFaturamentoConsolidada: number;
  itens: DesempenhoConsolidadoItem[];
  ajustesIndividuais: DesempenhoConsolidadoItem[];
  porEmpresa: Array<{
    tenantId: number;
    tenantNome: string;
    tenantTipo?: string;
    atingimentoMedio: number;
    configs: number;
    ajustesIndividuais: number;
    metasBatidas: number;
    faturamento: number;
    metaFaturamento: number;
  }>;
}

async function buscarRealizadoConfig(
  tenantId: number,
  grupo: string,
  tipo: TipoDesempenho,
  dataInicio: string,
  dataFim: string,
  refDate: string,
  profileId?: string
): Promise<{ valor: number; reunioes: number; fixacao: number }> {
  const rpcName = profileId
    ? 'calcular_realizado_desempenho_individual'
    : 'calcular_realizado_desempenho';
  const { data, error } = await (supabase as any).rpc(rpcName, {
    p_tenant_id: tenantId,
    p_grupo: grupo,
    p_tipo_desempenho: tipo,
    ...(profileId && { p_profile_id: profileId }),
    p_data_inicio: dataInicio,
    p_data_fim: dataFim,
    p_ref_date: refDate,
    p_matriz_tenant_id: 1,
  });

  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    valor: Number(row?.realizado_valor ?? 0),
    reunioes: Number(row?.realizado_reunioes ?? 0),
    fixacao: Number(row?.percentual_fixacao ?? 0),
  };
}

function formatarMetaRealizado(
  tipo: TipoDesempenho,
  metaValor: number,
  metaReunioes: number,
  metaFixacao: number,
  realizado: { valor: number; reunioes: number; fixacao: number },
  faixaAtingida?: DesempenhoFaixa | null
): {
  metaExibicao: string;
  realizadoExibicao: string;
  metaNumerica: number;
  realizadoNumerico: number;
  atingimento: number;
  atingiuMeta: boolean;
} {
  const campos = CAMPOS_META_POR_TIPO[tipo];
  const { valor: faturamento, reunioes, fixacao } = realizado;

  if (campos.fixacao) {
    const atingimento = calcularAtingimento(fixacao, metaFixacao);
    const faixaLabel = faixaAtingida ? ` · ${labelFaixaCurta(faixaAtingida.nivel)}` : '';
    return {
      metaExibicao: `${metaFixacao}% fixação`,
      realizadoExibicao: `${fixacao}%${faixaLabel}`,
      metaNumerica: metaFixacao,
      realizadoNumerico: fixacao,
      atingimento,
      atingiuMeta: metaFixacao > 0 && atingimento >= 100,
    };
  }

  if (campos.valor && campos.reunioes) {
    const atingValor = calcularAtingimento(faturamento, metaValor);
    const atingReunioes = calcularAtingimento(reunioes, metaReunioes);
    const atingimento = Math.min(atingValor, atingReunioes);
    const faixaLabel = faixaAtingida ? ` · ${labelFaixaCurta(faixaAtingida.nivel)}` : '';
    return {
      metaExibicao: `${faturamentoFormat(metaValor)} · ${metaReunioes} reuniões`,
      realizadoExibicao: `${faturamentoFormat(faturamento)} · ${reunioes} reuniões${faixaLabel}`,
      metaNumerica: metaValor,
      realizadoNumerico: faturamento,
      atingimento,
      atingiuMeta: metaValor > 0 && metaReunioes > 0 && atingimento >= 100,
    };
  }

  if (campos.reunioes) {
    const atingimento = calcularAtingimento(reunioes, metaReunioes);
    return {
      metaExibicao: `${metaReunioes} reuniões`,
      realizadoExibicao: `${reunioes} reuniões`,
      metaNumerica: metaReunioes,
      realizadoNumerico: reunioes,
      atingimento,
      atingiuMeta: metaReunioes > 0 && atingimento >= 100,
    };
  }

  const atingimento = calcularAtingimento(faturamento, metaValor);
  const faixaLabel = faixaAtingida ? ` · ${labelFaixaCurta(faixaAtingida.nivel)}` : '';
  return {
    metaExibicao: faturamentoFormat(metaValor),
    realizadoExibicao: `${faturamentoFormat(faturamento)}${faixaLabel}`,
    metaNumerica: metaValor,
    realizadoNumerico: faturamento,
    atingimento,
    atingiuMeta: metaValor > 0 && atingimento >= 100,
  };
}

function faturamentoFormat(valor: number): string {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

export async function buscarDesempenhoConsolidado(
  periodoMes: number,
  periodoAno: number
): Promise<DesempenhoConsolidadoResumo> {
  const periodoLabel = formatPeriodoProducao(periodoMes + 1, periodoAno);
  const performanceDb = supabase as any;

  const [configsResult, ajustesResult, tenantsResult] = await Promise.all([
    performanceDb
      .from('desempenho_config')
      .select('*, tenants(nome, tipo_unidade)')
      .is('profile_id', null)
      .eq('periodo_mes', periodoMes + 1)
      .eq('periodo_ano', periodoAno)
      .order('tenant_id')
      .order('grupo')
      .order('tipo_desempenho'),
    performanceDb
      .from('desempenho_config')
      .select('*, tenants(nome, tipo_unidade)')
      .not('profile_id', 'is', null)
      .eq('periodo_mes', periodoMes + 1)
      .eq('periodo_ano', periodoAno)
      .order('tenant_id')
      .order('tipo_desempenho'),
    supabase
      .from('tenants')
      .select('id, nome, tipo_unidade, status')
      .eq('status', 'ATIVO')
      .order('nome'),
  ]);

  if (configsResult.error) throw configsResult.error;
  if (ajustesResult.error) throw ajustesResult.error;
  if (tenantsResult.error) throw tenantsResult.error;

  const tenants = tenantsResult.data || [];
  const tenantIdsAtivos = new Set(tenants.map(tenant => Number(tenant.id)));
  const rows = (configsResult.data || []).filter((row: any) =>
    tenantIdsAtivos.has(Number(row.tenant_id))
  );
  const ajustesRows = (ajustesResult.data || []).filter((row: any) =>
    tenantIdsAtivos.has(Number(row.tenant_id))
  );
  const profileIds = [
    ...new Set(ajustesRows.map((row: any) => row.profile_id).filter(Boolean)),
  ] as string[];

  const profilesById = new Map<string, string>();
  if (profileIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, nome')
      .in('id', profileIds);
    if (profilesError) throw profilesError;
    for (const profile of profiles || []) {
      profilesById.set(profile.id, profile.nome || 'Membro sem nome');
    }
  }

  const tenantIds = [
    ...new Set([...rows, ...ajustesRows].map((row: any) => Number(row.tenant_id))),
  ];

  const { data: tenantSettings } = await supabase
    .from('tenant_settings')
    .select('tenant_id, dia_inicio_producao')
    .in('tenant_id', tenantIds.length > 0 ? tenantIds : [-1]);

  const startDayByTenant = new Map(
    (tenantSettings || []).map(s => [s.tenant_id, s.dia_inicio_producao ?? 1])
  );

  const periodoPorTenant = new Map<number, { dataInicio: string; dataFim: string; refDate: string }>();
  for (const tenantId of tenantIds) {
    const startDay = startDayByTenant.get(tenantId) ?? 1;
    const { startDate, endDate } = getProductionPeriod(periodoMes, periodoAno, startDay);
    periodoPorTenant.set(tenantId, {
      dataInicio: startDate.toISOString(),
      dataFim: endDate.toISOString(),
      refDate: endDate.toISOString().split('T')[0],
    });
  }

  const realizadoPorConfig = new Map<string, { valor: number; reunioes: number; fixacao: number }>();
  const faixaPorConfig = new Map<string, DesempenhoFaixa | null>();

  const linhasAtivas = [
    ...rows.filter((row: any) => row.ativo !== false),
    ...ajustesRows.filter((row: any) => row.ativo !== false),
  ];

  await Promise.all(
    linhasAtivas.map(async (row: any) => {
        const configId = String(row.id);
        const tipo = row.tipo_desempenho as TipoDesempenho;
        const periodo = periodoPorTenant.get(row.tenant_id);
        if (!periodo) return;

        const realizado = await buscarRealizadoConfig(
          row.tenant_id,
          row.grupo,
          tipo,
          periodo.dataInicio,
          periodo.dataFim,
          periodo.refDate,
          row.profile_id ?? undefined
        );
        realizadoPorConfig.set(configId, realizado);

        if (tipoUsaFaixas(tipo)) {
          const { data: faixa, error: faixaError } = await supabase.rpc('classificar_faixa_atingida', {
            p_desempenho_config_id: row.id,
            p_realizado_valor: realizado.valor,
            p_realizado_reunioes: realizado.reunioes,
            p_realizado_fixacao: realizado.fixacao,
          });
          if (faixaError) throw faixaError;
          const faixaRow = Array.isArray(faixa) ? faixa[0] : faixa;
          faixaPorConfig.set(configId, faixaRow ? parseFaixaRow(faixaRow as Record<string, unknown>) : null);
        }
      })
  );

  const mapRow = (row: any, escopo: 'cargo' | 'individual'): DesempenhoConsolidadoItem => {
      const tipo = row.tipo_desempenho as TipoDesempenho;
      const configId = String(row.id);
      const realizado = realizadoPorConfig.get(configId) ?? { valor: 0, reunioes: 0, fixacao: 0 };
      const faixaAtingida = faixaPorConfig.get(configId) ?? null;
      const metaValor = Number(row.meta_valor ?? 0);
      const metaReunioes = Number(row.meta_reunioes ?? 0);
      const metaFixacao = Number(row.meta_fixacao ?? 0);

      const formatado = formatarMetaRealizado(
        tipo,
        metaValor,
        metaReunioes,
        metaFixacao,
        realizado,
        faixaAtingida
      );

      const bonusFaixa = faixaAtingida?.bonus_percentual;

      return {
        configId,
        tenantId: row.tenant_id,
        tenantNome: (row.tenants as { nome?: string } | null)?.nome ?? `Empresa #${row.tenant_id}`,
        tenantTipo: (row.tenants as { tipo_unidade?: string } | null)?.tipo_unidade,
        grupo: row.grupo as GrupoDesempenho,
        tipoDesempenho: tipo,
        escopo,
        profileId: row.profile_id ?? undefined,
        profileNome: row.profile_id ? profilesById.get(row.profile_id) ?? 'Membro removido' : undefined,
        periodoLabel: formatPeriodoProducao(row.periodo_mes, row.periodo_ano),
        metaExibicao: formatado.metaExibicao,
        realizadoExibicao: formatado.realizadoExibicao,
        metaNumerica: formatado.metaNumerica,
        realizadoNumerico: formatado.realizadoNumerico,
        atingimento: formatado.atingimento,
        atingiuMeta: formatado.atingiuMeta,
        comissaoPercentualBase: Number(row.comissao_percentual_base ?? 0),
        bonusFaixaPercentual: bonusFaixa ?? 0,
        faixaAtingida,
      };
  };

  const itens: DesempenhoConsolidadoItem[] = rows
    .filter((row: any) => row.ativo !== false)
    .map((row: any) => mapRow(row, 'cargo'));
  const ajustesIndividuais: DesempenhoConsolidadoItem[] = ajustesRows
    .filter((row: any) => row.ativo !== false)
    .map((row: any) => mapRow(row, 'individual'));

  const metasBatidas = itens.filter(i => i.atingiuMeta).length;
  const abaixoDaMeta = itens.filter(i => i.metaNumerica > 0 && !i.atingiuMeta).length;
  const atingimentoMedio =
    itens.length > 0
      ? Math.round((itens.reduce((s, i) => s + i.atingimento, 0) / itens.length) * 100) / 100
      : 0;

  const faturamentoPorGrupo = new Map<string, number>();
  const metaPrincipalPorGrupo = new Map<string, DesempenhoConsolidadoItem>();
  const prioridadeMeta = (item: DesempenhoConsolidadoItem) => {
    if (item.grupo === 'representacao') return item.tipoDesempenho === 'REPRESENTANTE' ? 0 : 1;
    if (item.grupo === 'associado') return item.tipoDesempenho === 'ASSOCIADO' ? 0 : 1;
    if (item.grupo === 'time_interno') return item.tipoDesempenho === 'SUPERVISOR' ? 0 : 1;
    return 1;
  };

  for (const item of itens) {
    if (!CAMPOS_META_POR_TIPO[item.tipoDesempenho].valor) continue;
    const key = `${item.tenantId}:${item.grupo}`;
    if (!faturamentoPorGrupo.has(key)) {
      faturamentoPorGrupo.set(key, item.realizadoNumerico);
    }
    const atual = metaPrincipalPorGrupo.get(key);
    if (!atual || prioridadeMeta(item) < prioridadeMeta(atual)) {
      metaPrincipalPorGrupo.set(key, item);
    }
  }

  const faturamentoConsolidado = [...faturamentoPorGrupo.values()].reduce((s, v) => s + v, 0);
  const metaFaturamentoConsolidada = [...metaPrincipalPorGrupo.values()].reduce(
    (s, item) => s + item.metaNumerica,
    0
  );

  const porEmpresaMap = new Map<
    number,
    DesempenhoConsolidadoResumo['porEmpresa'][0] & { gruposVistos: Set<string> }
  >();

  for (const tenant of tenants) {
    porEmpresaMap.set(Number(tenant.id), {
      tenantId: Number(tenant.id),
      tenantNome: tenant.nome,
      tenantTipo: tenant.tipo_unidade ?? undefined,
      atingimentoMedio: 0,
      configs: 0,
      ajustesIndividuais: 0,
      metasBatidas: 0,
      faturamento: 0,
      metaFaturamento: 0,
      gruposVistos: new Set<string>(),
    });
  }

  for (const item of itens) {
    const atual = porEmpresaMap.get(item.tenantId) ?? {
      tenantId: item.tenantId,
      tenantNome: item.tenantNome,
      tenantTipo: item.tenantTipo,
      atingimentoMedio: 0,
      configs: 0,
      ajustesIndividuais: 0,
      metasBatidas: 0,
      faturamento: 0,
      metaFaturamento: 0,
      gruposVistos: new Set<string>(),
    };
    atual.configs += 1;
    if (item.atingiuMeta) atual.metasBatidas += 1;
    if (CAMPOS_META_POR_TIPO[item.tipoDesempenho].valor && !atual.gruposVistos.has(item.grupo)) {
      atual.gruposVistos.add(item.grupo);
      const key = `${item.tenantId}:${item.grupo}`;
      atual.faturamento += faturamentoPorGrupo.get(key) ?? 0;
      atual.metaFaturamento += metaPrincipalPorGrupo.get(key)?.metaNumerica ?? 0;
    }
    porEmpresaMap.set(item.tenantId, atual);
  }

  for (const ajuste of ajustesIndividuais) {
    const atual = porEmpresaMap.get(ajuste.tenantId);
    if (atual) atual.ajustesIndividuais += 1;
  }

  const porEmpresa = [...porEmpresaMap.values()].map(({ gruposVistos: _, ...empresa }) => {
    const itensEmpresa = itens.filter(i => i.tenantId === empresa.tenantId);
    return {
      ...empresa,
      atingimentoMedio:
        itensEmpresa.length > 0
          ? Math.round(
              (itensEmpresa.reduce((s, i) => s + i.atingimento, 0) / itensEmpresa.length) * 100
            ) / 100
          : 0,
    };
  });
  const tenantsComMetas = porEmpresa.filter(empresa => empresa.configs > 0).length;

  return {
    periodoLabel,
    totalTenants: porEmpresa.length,
    tenantsComMetas,
    tenantsSemMetas: Math.max(0, porEmpresa.length - tenantsComMetas),
    totalConfigs: rows.length,
    configsAtivas: itens.length,
    totalAjustesIndividuais: ajustesIndividuais.length,
    atingimentoMedio,
    metasBatidas,
    abaixoDaMeta,
    faturamentoConsolidado,
    metaFaturamentoConsolidada,
    itens,
    ajustesIndividuais,
    porEmpresa: porEmpresa.sort((a, b) => b.atingimentoMedio - a.atingimentoMedio),
  };
}

export { TIPO_DESEMPENHO_LABELS, GRUPO_DESEMPENHO_LABELS };
